import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import './ManageUsers.css';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState("");
  const [showInactive, setShowInactive] = useState(false); // Default to unchecked
  const [selectedRole, setSelectedRole] = useState("");
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers(pagination.currentPage);
  }, []);

  const fetchUsers = async (pageNumber = 1, searchTerm = null, showInactiveValue = null, roleId = null) => {
    try {
      setLoading(true);
      
      // Build query string
      let queryString = `pageNumber=${pageNumber}`;
      
      // Handle search criteria
      if (searchTerm !== null) {
        queryString += searchTerm ? `&searchCriteria=${encodeURIComponent(searchTerm)}` : '';
      } else if (searchCriteria) {
        queryString += `&searchCriteria=${encodeURIComponent(searchCriteria)}`;
      }
      
      // Handle inactive filter - unchecked = 1, checked = 0
      const inactiveValue = showInactiveValue !== null ? showInactiveValue : showInactive;
      const onlyActiveParam = inactiveValue ? 0 : 1; // unchecked (false) = 1, checked (true) = 0
      queryString += `&onlyActive=${onlyActiveParam}`;
      
      // Handle role filter
      const roleValue = roleId !== null ? roleId : (selectedRole ? parseInt(selectedRole) : null);
      if (roleValue) {
        queryString += `&roleId=${roleValue}`;
      }
      
      const data = await apiService.get(`/users?${queryString}`);
      
      // Handle new response structure with pagination and roles
      if (data.users && data.pagination) {
        setUsers(data.users);
        setPagination(data.pagination);
        
        // Update roles if they exist in response
        if (data.roles) {
          setRoles(data.roles);
        }
      } else {
        // Fallback for backward compatibility (if API returns old format)
        setUsers(Array.isArray(data) ? data : []);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSearch = () => {
    // Reset to page 1 when filtering
    fetchUsers(1);
  };

  const handleClear = async () => {
    // Clear search criteria and reset filters
    setSearchCriteria("");
    setShowInactive(false);
    setSelectedRole("");
    
    // Call endpoint with only pageNumber and onlyActive=1 (unchecked = 1)
    try {
      setLoading(true);
      const data = await apiService.get('/users?pageNumber=1&onlyActive=1');
      
      // Handle new response structure with pagination
      if (data.users && data.pagination) {
        setUsers(data.users);
        setPagination(data.pagination);
        
        // Update roles if they exist in response
        if (data.roles) {
          setRoles(data.roles);
        }
      } else {
        // Fallback for backward compatibility
        setUsers(Array.isArray(data) ? data : []);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchUsers(newPage);
    }
  };

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`);
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Manage Users</h1>
        </div>
        <div className="loading-state">
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Manage Users</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchUsers} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>Manage Users</h1>
        {pagination.totalCount > 0 && (
          <div className="pagination-info">
            Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} users
          </div>
        )}
      </div>

      {/* Filter and Search Controls */}
      <div className="filter-controls">
        <div className="filter-left">
          <label htmlFor="keywordSearch">Keyword search:</label>
          <input
            type="text"
            id="keywordSearch"
            className="keyword-input"
            value={searchCriteria}
            onChange={(e) => setSearchCriteria(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleFilterSearch();
              }
            }}
            placeholder="Enter search term"
          />
          <button
            type="button"
            onClick={handleFilterSearch}
            className="filter-search-btn"
          >
            Filter / search
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="clear-btn"
          >
            Clear
          </button>
        </div>
        
        <div className="filter-right">
          <label htmlFor="showInactive" className="checkbox-label">
            <input
              type="checkbox"
              id="showInactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive users
          </label>
        </div>
      </div>

      <div className="properties-table">
        <table>
          <thead>
            {/* Filter dropdowns row */}
            <tr className="filter-row">
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th>
                <select
                  id="roleFilter"
                  className="column-filter-select"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <option value="">All Roles</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.title}
                    </option>
                  ))}
                </select>
              </th>
              <th></th>
              <th></th>
            </tr>
            {/* Column headers row */}
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Agency</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>{user.id}</strong>
                </td>
                <td>
                  <a 
                    href="#"
                    className="property-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleUserClick(user.id);
                    }}
                    title="Click to edit user details"
                  >
                    {user.firstName} {user.lastName}
                  </a>
                </td>
                <td>{user.emailAddress}</td>
                <td>{user.cellNumber}</td>
                <td>
                  <span className="category-badge">
                    {user.userRoleType || 'N/A'}
                  </span>
                </td>
                <td>{user.agencyName || 'N/A'}</td>
                <td>
                  <span className={`active-badge ${user.isActive ? "active" : "inactive"}`}>
                    {user.isActive ? "Yes" : "No"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <div className="pagination-pages">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => {
              // Show first page, last page, current page, and pages around current
              const showPage = 
                pageNum === 1 ||
                pageNum === pagination.totalPages ||
                (pageNum >= pagination.currentPage - 2 && pageNum <= pagination.currentPage + 2);
              
              if (!showPage) {
                // Show ellipsis
                if (pageNum === pagination.currentPage - 3 || pageNum === pagination.currentPage + 3) {
                  return <span key={pageNum} className="pagination-ellipsis">...</span>;
                }
                return null;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`pagination-btn ${pageNum === pagination.currentPage ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
